import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import cmmsMessages from "../../cmms/cmmsMessages";
import messages from "../messages";
import { Box, Button, FormControlLabel, MenuItem, Radio, RadioGroup, Select, Stack, TextField, Typography, useTheme } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import Popup from "../../../components/Popup";
import AxionControls from "../../../components/controls/AxionControls";
import {
  createRecurringSchedule,
  getRecurringScheduleById,
  updateRecurringSchedule,
} from "../../../fetchers/cmmsRecurringSchedule";
import { fetchAssetHierarchyTree } from "../../../fetchers/cmmsAssetHierarchy";
import { fetchTools } from "../../../fetchers/tools";
import { fetchUsers } from "../../../fetchers/user";
import { getTrades } from "../../../fetchers/cmmsTrade";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { flattenAssetNodes } from "../../cmms/assetHierarchyUtils";
import {
  findTrade,
  getPrefillTradeId,
  isTradeMember,
  toTradeAssigneeOptions,
  toTradeOptions,
} from "../../cmms/cmmsTradeAssignment";
import { formatCmmsError } from "../../cmms/components/CmmsStates";
import { toDayjsLocale } from "../../cmms/cmmsDateLocale";
import { asUtcIso, formatTimestamp } from "../../cmms/cmmsFormat";
import {
  formatEstimatedHoursInput,
  parseEstimatedHours,
  validateEstimatedHours,
} from "../../cmms/cmmsEstimatedHours";
import {
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getMenuSx,
  getOutlinedButtonSx,
  getRadioSx,
  getSelectSx,
} from "../../cmms/assetHierarchyStyles";
import {
  WorkOrderPriority,
  WorkOrderType,
  getWorkOrderPriorityOptions,
  getWorkOrderTypeOptions,
} from "../../cmmsWorkOrders/workOrderEnums";
import {
  FrequencyType,
  ScheduleTriggerBasis,
  getDayOfWeekOptions,
  getFrequencyTypeOptions,
  getTriggerBasisOptions,
} from "../scheduleEnums";

// "asset" or "tool". A schedule that names a tool is measured against that tool's consumption
// wherever it ran, and reset by a work order closing against it rather than by the sweep.
const SCOPE_ASSET = "asset";
const SCOPE_TOOL = "tool";

const emptyForm = {
  scope: SCOPE_ASSET,
  toolId: null,
  warnAtPercent: "80",
  title: "",
  description: "",
  assetNodeId: null,
  workOrderType: WorkOrderType.PreventiveMaintenance,
  priority: WorkOrderPriority.Medium,
  assignedDepartmentId: null,
  tradeId: null,
  assignedResourceId: null,
  estimatedHours: "",
  triggerBasis: ScheduleTriggerBasis.Calendar,
  frequencyType: FrequencyType.Weekly,
  frequencyInterval: 1,
  dayOfWeek: 1,
  dayOfMonth: 1,
  runningTimeInterval: "",
  quantityInterval: "",
  /*
   * 08:00 local, not local midnight.
   *
   * startOf("day") serialises to 22:00Z or 23:00Z on the *previous* calendar day in Sweden, and
   * the backend reads the weekday off that UTC value (CmmsRecurringScheduleCalculator
   * .AlignToDayOfWeek) — so "Thursday" was read as Wednesday and aligned to Friday. Any hour that
   * keeps the UTC date equal to the local date removes the drift; 08:00 also reads as the start
   * of a shift rather than as a placeholder.
   */
  startDate: dayjs().startOf("day").hour(8).toISOString(),
  endDate: null,
};

/*
 * There is no intervalStartingDate here any more, and putting it back would be a mistake.
 *
 * The form carried a picker for it that had never worked: neither request view model declares the
 * property, so AutoMapper dropped it and ApplyBasis overwrote the field regardless. It was the
 * control someone reached for to reset a counter, and repairing it would have made an ordinary
 * edit able to reset one silently — the exact defect BE-15.19 replaced with an explicit reset
 * that writes a record. Resetting lives in the counter drawer on the schedule row.
 */

// asUtcIso on the way in: the API sends UTC without a trailing Z, so a bare dayjs(value) would
// read it as local and show the picker a time two hours behind what was saved.
const toDayjs = (value) => (value ? dayjs(asUtcIso(value)) : null);
// ...and on the way out too, for the same reason. The load effect seeds form state with the raw
// API string, and onChange only replaces it with a Dayjs if the user actually touches the picker.
// Saving an untouched field would otherwise re-read that naive string as local and write the
// instant back shifted by the local offset — every edit-save drifting it one offset further.
const toIso = (value) => {
  if (!value) return null;
  const d = dayjs(asUtcIso(value));
  return d.isValid() ? d.toISOString() : null;
};

export default function RecurringScheduleFormDialog({ open, onClose, scheduleId, companyKey, onSuccess }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const dateLocale = toDayjsLocale(intl.locale);
  const isEdit = Boolean(scheduleId);

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const treeQuery = useQuery(
    ["cmmsAssetHierarchyTree", companyKey, false],
    () => fetchAssetHierarchyTree(companyKey, false),
    { enabled: open && Boolean(companyKey), staleTime: 300000 },
  );

  const usersQuery = useQuery(["users"], () => fetchUsers(), { enabled: open, staleTime: 300000 });

  // The work order form's key, so the two share a cache entry and the Yrkesgrupper tab's
  // invalidation reaches both. Retired trades included, so a schedule already on one can show it.
  const tradesQuery = useQuery(
    ["cmmsTrades", companyKey, true],
    () => getTrades(companyKey, true),
    { enabled: open && Boolean(companyKey), staleTime: 30000 },
  );

  // Same query key the tool page and the activity form use, so the list comes from cache.
  const toolsQuery = useQuery(["tools", companyKey], () => fetchTools(companyKey), {
    enabled: open && Boolean(companyKey),
    staleTime: 300000,
  });

  const scheduleQuery = useQuery(
    ["cmmsRecurringSchedule", companyKey, scheduleId],
    () => getRecurringScheduleById(companyKey, scheduleId),
    { enabled: open && isEdit && Boolean(companyKey), staleTime: 0 },
  );

  const flatNodes = useMemo(() => flattenAssetNodes(treeQuery.data), [treeQuery.data]);
  const assetOptions = useMemo(
    () => flatNodes.map((node) => ({ id: node.id, title: node.name })),
    [flatNodes],
  );
  const departmentOptions = useMemo(
    () => flatNodes.filter((node) => node.nodeType === 2).map((node) => ({ id: node.id, title: node.name })),
    [flatNodes],
  );

  const trades = tradesQuery.data;
  const selectedTrade = useMemo(() => findTrade(trades, form.tradeId), [trades, form.tradeId]);

  const tradeOptions = useMemo(
    () => toTradeOptions(trades, form.tradeId, (name) => intl.formatMessage(messages.fieldTradeRetired, { name })),
    [trades, form.tradeId, intl],
  );

  /*
   * Trade first, then a person, as on the work order form: with a trade picked, only its members,
   * since the server refuses anyone else with CMMS_ASSIGNEE_NOT_IN_TRADE. Without one, the
   * technicians — every order this schedule raises is work somebody has to claim.
   */
  const userOptions = useMemo(
    () => toTradeAssigneeOptions(usersQuery.data, selectedTrade, form.assignedResourceId),
    [usersQuery.data, selectedTrade, form.assignedResourceId],
  );

  // "name - description", as the activity module renders the same register: tool names are
  // often bare numbers, and the description is what tells 1176 from 1178.
  const toolOptions = useMemo(
    () => (toolsQuery.data ?? []).map((tool) => ({
      id: tool.id,
      title: tool.description ? `${tool.name} - ${tool.description}` : tool.name,
    })),
    [toolsQuery.data],
  );

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      if (scheduleQuery.data) {
        const s = scheduleQuery.data;
        setForm({
          scope: s.toolId ? SCOPE_TOOL : SCOPE_ASSET,
          toolId: s.toolId ?? null,
          warnAtPercent: String(s.warnAtPercent ?? 80),
          title: s.title ?? "",
          description: s.description ?? "",
          assetNodeId: s.assetNodeId ?? null,
          workOrderType: s.workOrderType,
          priority: s.priority,
          assignedDepartmentId: s.assignedDepartmentId ?? null,
          tradeId: s.tradeId ?? null,
          assignedResourceId: s.assignedResourceId ?? null,
          // The number as saved; formatEstimatedHoursInput puts it in the reader's notation.
          estimatedHours: s.estimatedHours ?? "",
          triggerBasis: s.triggerBasis ?? ScheduleTriggerBasis.Calendar,
          frequencyType: s.frequencyType ?? FrequencyType.Weekly,
          frequencyInterval: s.frequencyInterval ?? 1,
          dayOfWeek: s.dayOfWeek ?? 1,
          dayOfMonth: s.dayOfMonth ?? 1,
          runningTimeInterval: s.runningTimeInterval ?? "",
          quantityInterval: s.quantityInterval ?? "",
          startDate: s.startDate,
          endDate: s.endDate ?? null,
        });
      }
    } else {
      setForm(emptyForm);
    }
    setErrors({});
  }, [open, isEdit, scheduleQuery.data]);

  const handleField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
  };

  /*
   * The work order form's pre-fill: a new department brings its trade along when exactly one
   * serves it and the person already named is in it. Only on a change made here, never when an
   * edit loads. Leaving it empty is a choice too — the sweep then asks the department each time it
   * generates — but filling it in shows the manager who will be told.
   */
  const handleDepartmentChange = (departmentId) => {
    setForm((current) => {
      const prefill = getPrefillTradeId(departmentId, current.assignedResourceId, trades, flatNodes);
      return {
        ...current,
        assignedDepartmentId: departmentId,
        ...(prefill != null ? { tradeId: prefill } : {}),
      };
    });
  };

  // A person outside the new trade is taken off, as on the work order form; clearing the trade
  // keeps them.
  const handleTradeChange = (tradeId) => {
    setForm((current) => {
      const trade = findTrade(trades, tradeId);
      const keepsAssignee = !trade || current.assignedResourceId == null
        || isTradeMember(trade, current.assignedResourceId);
      return {
        ...current,
        tradeId,
        ...(keepsAssignee ? {} : { assignedResourceId: null }),
      };
    });
  };

  const handleTextChange = (event) => {
    handleField(event.target.name || event.target.id, event.target.value);
  };

  const findOption = (options, id) => options.find((option) => option.id === id) ?? null;

  const isWeekly = form.frequencyType === FrequencyType.Weekly;
  const isMonthly = form.frequencyType === FrequencyType.Monthly;
  const isCalendar = form.triggerBasis === ScheduleTriggerBasis.Calendar;
  const isRunningTime = form.triggerBasis === ScheduleTriggerBasis.RunningTime;
  const isQuantity = form.triggerBasis === ScheduleTriggerBasis.Quantity;
  const isToolScope = form.scope === SCOPE_TOOL;

  const handleBasisChange = (value) => {
    const basis = Number(value);
    setForm((current) => ({
      ...current,
      triggerBasis: basis,
      // Clears the interval value for the basis being left. The anchor is not the form's to
      // touch: the backend re-anchors a usage schedule to now on a basis change, and that is the
      // one edit that still resets a counter as a side effect. It is deliberate and documented on
      // the server; everything else about resetting now goes through the counter drawer.
      runningTimeInterval: basis === ScheduleTriggerBasis.RunningTime ? current.runningTimeInterval : "",
      quantityInterval: basis === ScheduleTriggerBasis.Quantity ? current.quantityInterval : "",
    }));
  };

  const validate = () => {
    const next = {};
    if (!form.title?.trim()) next.title = intl.formatMessage(messages.titleRequired);

    // Required even on a tool schedule: it decides where the generated work order lands, and
    // a tool has no place in the maintenance hierarchy to answer that.
    if (!form.assetNodeId) next.assetNodeId = intl.formatMessage(messages.assetRequired);
    if (!form.startDate) next.startDate = intl.formatMessage(messages.startDateRequired);
    if (isToolScope && !form.toolId) next.toolId = intl.formatMessage(messages.toolRequired);
    const estimatedHoursError = validateEstimatedHours(form.estimatedHours, intl);
    if (estimatedHoursError) next.estimatedHours = estimatedHoursError;

    if (!isCalendar) {
      const warn = Number(form.warnAtPercent);
      if (!warn || warn < 1 || warn > 100) next.warnAtPercent = intl.formatMessage(messages.warnRange);
    }
    if (isCalendar) {
      const interval = Number(form.frequencyInterval);
      if (!interval || interval < 1) next.frequencyInterval = intl.formatMessage(messages.intervalMin);
      if (isMonthly) {
        const dom = Number(form.dayOfMonth);
        if (!dom || dom < 1 || dom > 31) next.dayOfMonth = intl.formatMessage(messages.dayOfMonthRange);
      }
    } else {
      const usageInterval = Number(isRunningTime ? form.runningTimeInterval : form.quantityInterval);
      if (!usageInterval || usageInterval < 1) {
        next.usageInterval = intl.formatMessage(messages.intervalRequired);
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        companyKey,
        assetNodeId: form.assetNodeId,
        title: form.title.trim(),
        description: form.description?.trim() || null,
        workOrderType: form.workOrderType,
        priority: form.priority,
        assignedDepartmentId: form.assignedDepartmentId || null,
        // Always sent, on update too: the server saves it as sent, so leaving it out would clear it.
        tradeId: form.tradeId || null,
        assignedResourceId: form.assignedResourceId || null,
        // Always sent, like the trade: saved as sent, so leaving it out would clear it. null is
        // an empty box, not zero.
        estimatedHours: parseEstimatedHours(form.estimatedHours),
        triggerBasis: form.triggerBasis,
        frequencyType: form.frequencyType,
        frequencyInterval: Number(form.frequencyInterval),
        dayOfWeek: isWeekly ? Number(form.dayOfWeek) : null,
        dayOfMonth: isMonthly ? Number(form.dayOfMonth) : null,
        runningTimeInterval: isRunningTime ? Number(form.runningTimeInterval) : null,
        quantityInterval: isQuantity ? Number(form.quantityInterval) : null,

        // Omitted on a calendar schedule, where it means nothing. On update an omitted value
        // means "keep the stored one", which is the right answer for a field this form is not
        // showing.
        warnAtPercent: isCalendar ? null : Number(form.warnAtPercent),
        startDate: toIso(form.startDate),
        endDate: toIso(form.endDate),
      };
      let result;
      if (isEdit) {
        // toolId is deliberately absent from the update payload — it is fixed at creation,
        // and the API surface cannot express the change at all.
        result = await updateRecurringSchedule(scheduleId, payload);
      } else {
        // No createdByUserId: the API stamps the creator from the token, and the request model
        // does not carry the field at all.
        result = await createRecurringSchedule({
          ...payload,
          toolId: isToolScope ? form.toolId : null,
        });
      }
      pushNotification(intl.formatMessage(isEdit ? messages.toastUpdated : messages.toastCreated));
      onSuccess?.(result);
      onClose?.();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.saveFailed)));
    } finally {
      setSubmitting(false);
    }
  };

  // formatTimestamp, not dayjs().toLocaleString() — Dayjs has no toLocaleString of its own, so
  // that call fell through to Object.prototype and printed a raw RFC-1123 GMT string.
  const nextRunReadOnly = scheduleQuery.data?.nextRunDate
    ? formatTimestamp(intl, scheduleQuery.data.nextRunDate)
    : null;

  return (
    <Popup
      openPopup={open}
      setOpenPopup={(value) => { if (!value) onClose?.(); }}
      maxWidth="md"
      title={intl.formatMessage(isEdit ? messages.editDialogTitle : messages.addDialogTitle)}
    >
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        <Box>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            <FormattedMessage {...messages.fieldScope} />
          </Typography>
          <RadioGroup
            row
            value={form.scope}
            onChange={(event) => handleField("scope", event.target.value)}
          >
            <FormControlLabel
              value={SCOPE_ASSET}
              control={<Radio size="small" sx={getRadioSx(cmmsColors)} />}
              label={intl.formatMessage(messages.scopeAsset)}
              // Switching an existing schedule between asset and tool is the same change as
              // repointing it at another tool, and is refused for the same reason.
              disabled={isEdit}
            />
            <FormControlLabel
              value={SCOPE_TOOL}
              control={<Radio size="small" sx={getRadioSx(cmmsColors)} />}
              label={intl.formatMessage(messages.scopeTool)}
              disabled={isEdit}
            />
          </RadioGroup>
        </Box>

        {isToolScope &&
          <Box>
            <AxionControls.AxionAutocomplete
              name="toolId"
              label={intl.formatMessage(messages.fieldToolRequired)}
              options={toolOptions}
              value={findOption(toolOptions, form.toolId)}
              onChange={(_event, option) => handleField("toolId", option?.id ?? null)}
              disabled={isEdit}
              error={errors.toolId}
            />
            {isEdit &&
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                <FormattedMessage {...messages.toolLockedHint} />
              </Typography>
            }
          </Box>
        }

        <Box>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            <FormattedMessage {...messages.fieldTriggerBasis} />
          </Typography>
          <RadioGroup
            row
            value={String(form.triggerBasis)}
            onChange={(event) => handleBasisChange(event.target.value)}
          >
            {getTriggerBasisOptions(intl).map((option) => (
              <FormControlLabel
                key={option.value}
                value={String(option.value)}
                control={<Radio size="small" sx={getRadioSx(cmmsColors)} />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </Box>
        <AxionControls.AxionTextBox
          name="title"
          label={intl.formatMessage(messages.fieldTitleRequired)}
          value={form.title}
          onChange={handleTextChange}
          error={errors.title}
        />
        <AxionControls.AxionMultiLineText
          name="description"
          label={intl.formatMessage(messages.fieldDescription)}
          value={form.description}
          rows={2}
          onChange={handleTextChange}
        />
        <AxionControls.AxionAutocomplete
          name="assetNodeId"
          label={intl.formatMessage(messages.fieldAssetRequired)}
          options={assetOptions}
          value={findOption(assetOptions, form.assetNodeId)}
          onChange={(_event, option) => handleField("assetNodeId", option?.id ?? null)}
          error={errors.assetNodeId}
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}><FormattedMessage {...messages.fieldType} /></Typography>
            <Select
              fullWidth
              size="small"
              value={form.workOrderType}
              onChange={(event) => handleField("workOrderType", event.target.value)}
              sx={getSelectSx(cmmsColors)}
              MenuProps={{ PaperProps: { sx: getMenuSx(cmmsColors) } }}
            >
              {getWorkOrderTypeOptions(intl).map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}><FormattedMessage {...messages.fieldPriority} /></Typography>
            <Select
              fullWidth
              size="small"
              value={form.priority}
              onChange={(event) => handleField("priority", event.target.value)}
              sx={getSelectSx(cmmsColors)}
              MenuProps={{ PaperProps: { sx: getMenuSx(cmmsColors) } }}
            >
              {getWorkOrderPriorityOptions(intl).map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </Box>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <AxionControls.AxionAutocomplete
              name="assignedDepartmentId"
              label={intl.formatMessage(messages.fieldAssignedDepartment)}
              options={departmentOptions}
              value={findOption(departmentOptions, form.assignedDepartmentId)}
              onChange={(_event, option) => handleDepartmentChange(option?.id ?? null)}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <AxionControls.AxionAutocomplete
              name="tradeId"
              label={intl.formatMessage(messages.fieldTrade)}
              options={tradeOptions}
              value={findOption(tradeOptions, form.tradeId)}
              onChange={(_event, option) => handleTradeChange(option?.id ?? null)}
            />
          </Box>
        </Stack>
        {/*
          * Said here because it is new: generated orders told nobody before trades. Now the
          * trade's technicians hear about each one — or, with no trade picked, those of the trades
          * serving the department, and still nobody where no trade does.
          */}
        <Typography variant="caption" sx={{ color: "text.secondary", mt: -1 }}>
          <FormattedMessage {...messages.tradeNotifyHint} />
        </Typography>
        <AxionControls.AxionAutocomplete
          name="assignedResourceId"
          label={intl.formatMessage(messages.fieldAssignedResource)}
          options={userOptions}
          value={findOption(userOptions, form.assignedResourceId)}
          onChange={(_event, option) => handleField("assignedResourceId", option?.id ?? null)}
        />

        {/*
          * Under the people because it is about the job each order is, like them, and not about
          * when the schedule runs. A text box rather than type="number", for the decimal comma —
          * see the work order form. Half width: a whole row for a four-digit number reads as a
          * place to write a note.
          */}
        <TextField
          label={intl.formatMessage(cmmsMessages.estimatedHoursLabel)}
          value={formatEstimatedHoursInput(intl, form.estimatedHours)}
          onChange={(event) => handleField("estimatedHours", event.target.value)}
          error={Boolean(errors.estimatedHours)}
          helperText={errors.estimatedHours ?? intl.formatMessage(messages.estimatedHoursHint)}
          inputProps={{ inputMode: "decimal" }}
          size="small"
          sx={{ width: { xs: "100%", sm: "50%" } }}
        />

        {isCalendar && (
          <>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}><FormattedMessage {...messages.fieldFrequency} /></Typography>
                <Select
                  fullWidth
                  size="small"
                  value={form.frequencyType}
                  onChange={(event) => handleField("frequencyType", event.target.value)}
                  sx={getSelectSx(cmmsColors)}
                  MenuProps={{ PaperProps: { sx: getMenuSx(cmmsColors) } }}
                >
                  {getFrequencyTypeOptions(intl).map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </Box>
              <Box sx={{ flex: 1 }}>
                <AxionControls.AxionTextBox
                  name="frequencyInterval"
                  label={intl.formatMessage(messages.fieldIntervalRequired)}
                  value={form.frequencyInterval}
                  onChange={handleTextChange}
                  error={errors.frequencyInterval}
                />
              </Box>
            </Stack>

            {isWeekly &&
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}><FormattedMessage {...messages.fieldDayOfWeek} /></Typography>
                <Select
                  fullWidth
                  size="small"
                  value={form.dayOfWeek ?? 1}
                  onChange={(event) => handleField("dayOfWeek", event.target.value)}
                  sx={getSelectSx(cmmsColors)}
                  MenuProps={{ PaperProps: { sx: getMenuSx(cmmsColors) } }}
                >
                  {getDayOfWeekOptions(intl).map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </Box>
            }
            {isMonthly &&
              <AxionControls.AxionTextBox
                name="dayOfMonth"
                label={intl.formatMessage(messages.fieldDayOfMonth)}
                value={form.dayOfMonth}
                onChange={handleTextChange}
                error={errors.dayOfMonth}
              />
            }
          </>
        )}

        {!isCalendar && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box sx={{ flex: 2 }}>
              <TextField
                label={intl.formatMessage(isRunningTime ? messages.fieldRunningTimeInterval : messages.fieldQuantityInterval)}
                required
                type="number"
                value={isRunningTime ? form.runningTimeInterval : form.quantityInterval}
                onChange={(event) =>
                  handleField(isRunningTime ? "runningTimeInterval" : "quantityInterval", event.target.value)
                }
                error={Boolean(errors.usageInterval)}
                helperText={errors.usageInterval}
                size="small"
                fullWidth
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextField
                label={intl.formatMessage(messages.fieldWarnAtPercent)}
                type="number"
                value={form.warnAtPercent}
                onChange={(event) => handleField("warnAtPercent", event.target.value)}
                error={Boolean(errors.warnAtPercent)}
                helperText={errors.warnAtPercent}
                size="small"
                fullWidth
              />
            </Box>
          </Stack>
        )}

        {/*
          * The two kinds are reset by different events, and this is where that is said. An
          * asset schedule's anchor is a plan the sweep advances; a tool schedule's is a fact —
          * when the tool was last serviced — that only a closing work order moves.
          *
          * Both strings now also name the third route, the explicit reset in the schedule list.
          * This is the last thing left in the form that talks about the counter, and the picker
          * that used to sit under it — the one that never worked — is what someone reached for
          * to reset one. Saying nothing here would leave them with no next move.
          */}
        {!isCalendar &&
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            <FormattedMessage {...(isToolScope ? messages.anchorResetOnClose : messages.anchorResetOnGenerate)} />
          </Typography>
        }

        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dateLocale}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <DateTimePicker
              label={intl.formatMessage(messages.fieldStartDateRequired)}
              value={toDayjs(form.startDate)}
              onChange={(value) => handleField("startDate", value)}
              renderInput={(params) => <TextField fullWidth size="small" {...params} error={Boolean(errors.startDate)} helperText={errors.startDate} />}
            />
            <DateTimePicker
              label={intl.formatMessage(messages.fieldEndDate)}
              value={toDayjs(form.endDate)}
              onChange={(value) => handleField("endDate", value)}
              renderInput={(params) => <TextField fullWidth size="small" {...params} />}
            />
          </Stack>
        </LocalizationProvider>

        {isEdit && isCalendar && nextRunReadOnly &&
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            <FormattedMessage {...messages.nextRunReadOnly} values={{ value: nextRunReadOnly }} />
          </Typography>
        }

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={submitting} sx={getOutlinedButtonSx(cmmsColors)}><FormattedMessage {...cmmsMessages.cancel} /></Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={getContainedButtonSx(cmmsColors)}>
            <FormattedMessage {...(isEdit ? cmmsMessages.save : cmmsMessages.add)} />
          </Button>
        </Stack>
      </Box>
      <AxionControls.AxionBackdrop open={submitting} />
    </Popup>
  );
}
